import { useEffect, useMemo, useState } from "react";
import { exportAdminLogs, getAdminLogs, purgeAdminLogs, type AdminLogItem, type AdminLogQuery } from "../api/adminLogsApi";
import { usePermissionCheck } from "../hooks/usePermissionCheck";
import AdminErrorState from "../Components/AdminErrorState";
import { confirmDialog, notifyDialog } from "../utils/dialogs";

const DEFAULT_PAGE_SIZE = 50;

export default function AdminLogs() {
  const { loading, allowed } = usePermissionCheck("Logs.View");
  const { allowed: canExport } = usePermissionCheck("Logs.Export");
  const { allowed: canPurge } = usePermissionCheck("Logs.Manage");
  const [filters, setFilters] = useState<AdminLogQuery>({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [logs, setLogs] = useState<AdminLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [fetching, setFetching] = useState(false);
  const [purging, setPurging] = useState(false);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / (filters.pageSize ?? DEFAULT_PAGE_SIZE))), [total, filters.pageSize]);

  useEffect(() => {
    if (!allowed) {
      return;
    }
    void loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, filters.page, filters.pageSize]);

  const loadLogs = async () => {
    setFetching(true);
    const response = await getAdminLogs(filters);
    setLogs(response.items);
    setTotal(response.total);
    setFetching(false);
    setExpandedLogId((current) => (response.items.some((log) => log.id === current) ? current : null));
  };

  const handleFilterChange = (key: keyof AdminLogQuery, value: string | number | undefined) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: 1,
    }));
  };

  const handleSeverityChip = (level?: number) => {
    handleFilterChange("severity", level);
  };

  const handleExport = async () => {
    const blob = await exportAdminLogs(filters);
    if (!blob) {
      await notifyDialog("Log export işlemi başarısız oldu.", "error");
      return;
    }
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `logs-${new Date().toISOString()}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handlePurge = async () => {
    const confirmed = await confirmDialog({
      title: "Tüm logları temizle",
      text: "Tüm log kayıtlarını temizlemek istediğine emin misin? Bu işlem geri alınamaz.",
      confirmText: "Evet, temizle",
      danger: true,
    });
    if (!confirmed) {
      return;
    }
    setPurging(true);
    const deleted = await purgeAdminLogs();
    setPurging(false);
    if (deleted === null) {
      await notifyDialog("Log temizleme işlemi başarısız oldu.", "error");
      return;
    }
    await notifyDialog(
      deleted === 0 ? "Silinecek log kaydı bulunamadı." : `${deleted} log kaydı silindi.`,
      "success",
    );
    void loadLogs();
  };

  const severitySummary = useMemo(() => {
    const summary = { info: 0, warning: 0, error: 0 };
    logs.forEach((log) => {
      if (log.severity === 2) summary.error += 1;
      else if (log.severity === 1) summary.warning += 1;
      else summary.info += 1;
    });
    return summary;
  }, [logs]);

  if (loading) {
    return <div className="p-8 text-center text-sm text-gray-500">Yetkiler doğrulanıyor...</div>;
  }

  if (!allowed) {
    return (
      <AdminErrorState
        status="warning"
        title="İzniniz yok"
        description="Log kayıtlarını görmek için gerekli izne sahip değilsiniz."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Log Kayıtları</h1>
          <p className="text-sm text-slate-500">İşlem geçmişini alan, aksiyon ya da kullanıcı bazında filtreleyebilirsin.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canPurge && (
            <button
              onClick={handlePurge}
              disabled={purging}
              className="rounded border border-red-300 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60"
            >
              {purging ? "Temizleniyor..." : "Logları temizle"}
            </button>
          )}
          {canExport && (
            <button
              onClick={handleExport}
              className="rounded bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700"
            >
              CSV dışa aktar
            </button>
          )}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <SummaryCard label="Bilgi" value={severitySummary.info} accent="bg-slate-100 text-slate-700" />
        <SummaryCard label="Uyarı" value={severitySummary.warning} accent="bg-amber-100 text-amber-700" />
        <SummaryCard label="Hata" value={severitySummary.error} accent="bg-red-100 text-red-700" />
      </div>

      <section className="rounded bg-white p-4 shadow">
        <div className="grid gap-3 md:grid-cols-3">
          <label className="text-xs text-gray-600">
            <div className="mb-1">Başlangıç</div>
            <input
              type="datetime-local"
              className="w-full rounded border p-2 text-sm"
              value={filters.startUtc ?? ""}
              onChange={(e) => handleFilterChange("startUtc", e.target.value || undefined)}
            />
          </label>
          <label className="text-xs text-gray-600">
            <div className="mb-1">Bitiş</div>
            <input
              type="datetime-local"
              className="w-full rounded border p-2 text-sm"
              value={filters.endUtc ?? ""}
              onChange={(e) => handleFilterChange("endUtc", e.target.value || undefined)}
            />
          </label>
          <label className="text-xs text-gray-600">
            <div className="mb-1">Alan</div>
            <input
              className="w-full rounded border p-2 text-sm"
              placeholder="economy, permissions..."
              value={filters.area ?? ""}
              onChange={(e) => handleFilterChange("area", e.target.value || undefined)}
            />
          </label>
          <label className="text-xs text-gray-600">
            <div className="mb-1">Aksiyon</div>
            <input
              className="w-full rounded border p-2 text-sm"
              placeholder="create, update..."
              value={filters.action ?? ""}
              onChange={(e) => handleFilterChange("action", e.target.value || undefined)}
            />
          </label>
          <label className="text-xs text-gray-600">
            <div className="mb-1">Kullanıcı ID</div>
            <input
              className="w-full rounded border p-2 text-sm"
              placeholder="GUID"
              value={filters.actorUserId ?? ""}
              onChange={(e) => handleFilterChange("actorUserId", e.target.value || undefined)}
            />
          </label>
          <label className="text-xs text-gray-600">
            <div className="mb-1">Önem seviyesi</div>
            <select
              className="w-full rounded border p-2 text-sm"
              value={filters.severity?.toString() ?? ""}
              onChange={(e) => handleFilterChange("severity",e.target.value ? Number(e.target.value) : undefined)}
            >
              <option value="">Tümü</option>
              <option value="0">Bilgi</option>
              <option value="1">Uyarı</option>
              <option value="2">Hata</option>
            </select>
          </label>
          <label className="text-xs text-gray-600 md:col-span-2">
            <div className="mb-1">Serbest arama</div>
            <input
              className="w-full rounded border p-2 text-sm"
              placeholder="Kullanıcı adı, hedef veya correlation id"
              value={filters.search ?? ""}
              onChange={(e) => handleFilterChange("search", e.target.value || undefined)}
            />
          </label>
          <div className="flex items-end gap-2">
            <button
              type="button"
              onClick={loadLogs}
              className="w-full rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500"
            >
              Filtrele
            </button>
            <button
              type="button"
              onClick={() => setFilters({ page: 1, pageSize: DEFAULT_PAGE_SIZE })}
              className="rounded border border-gray-300 px-3 py-2 text-xs text-gray-600 hover:bg-gray-50"
            >
              Sıfırla
            </button>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold text-slate-600">
          <FilterChip
            active={!filters.severity && !filters.area}
            label="Tümü"
            onClick={() => {
              handleSeverityChip(undefined);
              handleFilterChange("area", undefined);
            }}
          />
          <FilterChip active={filters.severity === 0} label="Bilgi" onClick={() => handleSeverityChip(0)} />
          <FilterChip active={filters.severity === 1} label="Uyarı" onClick={() => handleSeverityChip(1)} />
          <FilterChip active={filters.severity === 2} label="Hata" onClick={() => handleSeverityChip(2)} />
        </div>
      </section>

      <section className="rounded bg-white p-4 shadow">
        <div className="mb-2 text-xs text-gray-500">
          Toplam {total} kayıt | Sayfa {filters.page ?? 1} / {totalPages}
        </div>

        {fetching ? (
          <div className="text-sm text-gray-500">Loglar yükleniyor...</div>
        ) : logs.length === 0 ? (
          <div className="text-sm text-gray-500">Kayıt bulunamadı.</div>
        ) : (
          <div className="space-y-3">
            {logs.map((log) => {
              const expanded = expandedLogId === log.id;
              return (
                <article
                  key={log.id}
                  className="rounded-2xl border border-slate-200 p-4 text-sm shadow-sm hover:border-slate-300"
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <SeverityPill level={log.severity} />
                      <div>
                        <div className="text-xs text-slate-500">{formatLocalDate(log.occurredAtUtc)}</div>
                        <div className="text-sm font-semibold text-slate-900">
                          {log.area} <span className="text-slate-400">/</span> {log.action}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      {log.statusCode && <StatusPill code={log.statusCode} />}
                      <button
                        type="button"
                        onClick={() => copyToClipboard(log.correlationId)}
                        className="rounded-full border border-slate-300 px-3 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-100"
                      >
                        Correlation kopyala
                      </button>
                    </div>
                  </div>

                  <p className="mt-2 text-slate-700">{buildLogSummary(log)}</p>

                  <div className="mt-3 grid gap-2 text-xs text-slate-500 md:grid-cols-3">
                    <MetaItem label="Kullanıcı" value={log.actorName ?? "Sistem"} sub={log.actorRole ?? "-"} />
                    <MetaItem
                      label="IP / Hedef"
                      value={log.actorIp ?? "-"}
                      sub={
                        log.targetType
                          ? `${log.targetType}${log.targetId ? ` (${log.targetId})` : ""}`
                          : log.targetId ?? "-"
                      }
                    />
                    <MetaItem label="Correlation" value={log.correlationId} />
                  </div>

                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setExpandedLogId((current) => (current === log.id ? null : log.id))}
                      className="text-xs font-semibold text-blue-600 hover:text-blue-500"
                    >
                      {expanded ? "Detayı gizle" : "Detayı göster"}
                    </button>
                  </div>

                  {expanded && (
                    <div className="mt-3 space-y-2 rounded-xl bg-slate-50 p-3 text-xs text-slate-700">
                      <DetailRow label="User ID" value={log.actorUserId ?? "-"} />
                      <DetailRow label="Alan / Aksiyon" value={`${log.area} / ${log.action}`} />
                      <DetailRow label="HTTP Durumu" value={log.statusCode?.toString() ?? "-"} />
                      <div>
                        <div className="text-xs font-semibold uppercase text-slate-500">Ek Metadata</div>
                        <pre className="mt-1 max-h-60 overflow-auto rounded bg-slate-900/80 p-3 text-[11px] text-emerald-100">
{log.metadataJson ? formatJson(log.metadataJson) : "Metadata yok."}
                        </pre>
                      </div>
                    </div>
                  )}
                </article>
              
              );
            })}
            
          </div>
        )}

      </section>
      <div className="flex items-center justify-between text-sm">
        <div>
          Sayfa{" "}
          <strong>
            {filters.page ?? 1}/{totalPages}
          </strong>
        </div>
        <div className="flex gap-2">
          <button
            className="rounded border px-3 py-1 text-xs"
            disabled={(filters.page ?? 1) <= 1}
            onClick={() => setFilters((prev) => ({ ...prev, page: Math.max(1, (prev.page ?? 1) - 1) }))}
          >
            Önceki
          </button>
          <button
            className="rounded border px-3 py-1 text-xs"
            disabled={(filters.page ?? 1) >= totalPages}
            onClick={() => setFilters((prev) => ({ ...prev, page: Math.min(totalPages, (prev.page ?? 1) + 1) }))}
          >
            Sonraki
          </button>
        </div>
      </div>
    </div>
    
  );
}


function SeverityPill({ level }: { level: number }) {
  const map: Record<number, { label: string; className: string }> = {
    0: { label: "Bilgi", className: "bg-slate-100 text-slate-700" },
    1: { label: "Uyarı", className: "bg-amber-100 text-amber-700" },
    2: { label: "Hata", className: "bg-red-100 text-red-700" },
  };
  const item = map[level] ?? map[0];
  return <span className={`rounded-full px-2 py-1 text-xs ${item.className}`}>{item.label}</span>;
}

function StatusPill({ code }: { code: number }) {
  let tone = "bg-slate-100 text-slate-700";
  if (code >= 500) tone = "bg-red-100 text-red-700";
  else if (code >= 400) tone = "bg-amber-100 text-amber-700";
  else if (code >= 200) tone = "bg-emerald-100 text-emerald-700";
  return <span className={`rounded-full px-2 py-1 text-xs font-semibold ${tone}`}>HTTP {code}</span>;
}

function MetaItem({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div>
      <div className="text-[11px] uppercase text-slate-400">{label}</div>
      <div className="text-sm text-slate-800 break-all">{value || "-"}</div>
      {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
    </div>
  );
}

function SummaryCard({ label, value, accent }: { label: string; value: number; accent: string }) {
  return (
    <div className={`rounded border border-slate-200 p-3 text-sm ${accent}`}>
      <div className="text-xs uppercase">{label}</div>
      <div className="text-2xl font-semibold">{value}</div>
    </div>
  );
}

function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-3 py-1 text-xs font-semibold ${
        active ? "bg-slate-900 text-white" : "border border-slate-300 text-slate-600 hover:bg-slate-100"
      }`}
    >
      {label}
    </button>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase text-slate-500">{label}</div>
      <div className="text-sm text-slate-800 break-all">{value || "-"}</div>
    </div>
  );
}

function buildLogSummary(log: AdminLogItem) {
  const actor = log.actorName ?? "Sistem";
  const target = log.targetType
    ? `${log.targetType}${log.targetId ? ` (${log.targetId})` : ""}`
    : (log.targetId ?? "hedef belirtilmedi");
  const statusPart = log.statusCode ? ` ? HTTP ${log.statusCode}` : "";
  return `${actor}, ${log.action} i?lemini ${target} ?zerinde ger?ekle?tirdi${statusPart}.`;
}

function copyToClipboard(text: string) {
  if (!navigator?.clipboard) {
    return;
  }
  void navigator.clipboard.writeText(text);
}

function formatJson(value: string): string {
  try {
    const parsed = JSON.parse(value);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return value;
  }
}

function formatLocalDate(value: string) {
  const date = new Date(value);
  return date.toLocaleString("tr-TR", { hour12: false });
}
